import { MetricTarget } from '@bioliberty/lifemind';
import { AlertCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { comparisonMetricOptions } from '.';
import { ClinicInsights } from '../../api/clinics';
import { Alert, AlertTitle } from '../../components/Alert';
import { Card, CardContent, CardHeader } from '../../components/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/Input/Select';
import { ClinicsBarChart } from '../charts/ClinicsBarChart';

export interface ClinicsComparisonCardProps {
  data: ClinicInsights[];
  targets?: MetricTarget[];
  showTargets?: boolean;
  onShowTargetsClick?: () => void;
}

export function ClinicsComparisonCard({ data, targets, showTargets, onShowTargetsClick }: ClinicsComparisonCardProps) {
  const [dataKey, setDataKey] = useState<keyof ClinicInsights>('activePatientCount');

  const option = comparisonMetricOptions.find((option) => option.value === dataKey);

  const chartData = useMemo(
    () =>
      data.map((value) => ({ label: value.label, value: value[dataKey] as number })).sort((a, b) => b.value - a.value),
    [data, dataKey]
  );

  const chartReferenceLines = useMemo(() => {
    if (!option || !showTargets) {
      return [];
    }

    const target = targets?.find((t) => t.metric === option.value);
    if (!target) {
      return [];
    }

    if (option?.value === 'activePatientCount') {
      return data
        ?.filter((c) => c.monthlyCapacity)
        .map((clinicInsight) => {
          const monthlyCapacity = clinicInsight.monthlyCapacity ?? 0;
          return {
            value: Math.round(monthlyCapacity * (target.value / 100)),
            label: `Target`,
            targetBarLabel: clinicInsight.label
          };
        });
    }

    return [{ value: option?.targetValueFormatter?.(target.value) ?? target.value, label: 'Target' }];
  }, [data, option, showTargets, targets]);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <Select onValueChange={setDataKey} value={dataKey as string}>
          <SelectTrigger className="m-0 w-[250px]" variant="light" data-testid="metric-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {comparisonMetricOptions.map((option) => (
              <SelectItem key={option.value} value={option.value as string}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {!showTargets && (
          <Alert className="w-fit">
            <AlertCircle className="stroke-accent w-4" />
            <AlertTitle className="flex flex-row gap-1">
              To see monthly targets,
              <span
                className="text-accent text-md flex cursor-pointer items-center justify-center hover:brightness-110"
                onClick={onShowTargetsClick}
              >
                click here to change your date range
              </span>
              to monthly.
            </AlertTitle>
          </Alert>
        )}
      </CardHeader>
      <CardContent>
        <ClinicsBarChart
          data={chartData}
          referenceLines={chartReferenceLines}
          labelFormatter={option?.labelFormatter}
          tickFormatter={option?.tickFormatter}
        />
      </CardContent>
    </Card>
  );
}
